<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useForm } from 'vee-validate';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import * as yup from '@/helpers/yup-distilled';

import { router, getNextRouteFromNewSigninState } from '@/helpers';
import { useAuthStore } from '@/stores';
import { getFriendlyErrorMessage } from '@/helpers';

const route = useRoute();

const schema = yup.object().shape({
	email: yup
		.string()
		.email('Must be a valid email address.')
		.required('Email address is required')
});

const { defineField, errors, isSubmitting, handleSubmit, setErrors } = useForm({
	validationSchema: schema
});

const [email] = defineField('email');

const authStore = useAuthStore();

const cancel = () => {
	authStore.signInState = 'init';
	router.push(getNextRouteFromNewSigninState(authStore.signInState, route));
};

const onSubmit = handleSubmit((values, { setErrors }) => {
	const { email } = values;

	return authStore
		.requestUserNamesByEmail(email)
		.then(async () => {
			router.push('/recoverUserName/done');
		})
		.catch(async (error) => {
			const errorOutput = await getFriendlyErrorMessage(error);
			setErrors({ apiError: errorOutput.message });
		});
});
</script>

<template>
	<form @submit="onSubmit">
		<div class="p-fluid login-small-form mb-8">
			<div class="mb-4">
				<span
					>Please enter the email address associated with your Aspire
					account. If we can find it, we'll send your username.</span
				>
			</div>
			<div class="p-field mt-2 mb-7">
				<label class="font-semibold" for="email">Email Address</label>
				<InputText
					v-model="email"
					autocomplete="off"
					autofocus
					type="text"
					:invalid="!!errors.email"
				/>
			</div>
			<div class="button-row mb-2">
				<div class="submit-button">
					<Button
						label="Submit"
						:disabled="isSubmitting || !email"
						type="submit"
					></Button>
				</div>
				<div class="cancel-button">
					<Button
						:disabled="isSubmitting"
						@click="cancel()"
						label="Cancel"
						severity="danger"
					></Button>
				</div>
			</div>
			<div class="p-error">{{ errors.email }}&nbsp;</div>

			<div v-if="errors.apiError" class="p-error">
				{{ errors.apiError }}
			</div>
		</div>
	</form>
</template>

<style scoped>
.button-row {
	display: flex;
	flex-direction: row;
}

.submit-button {
	flex: 3;
	padding-right: 0.5rem;
}

.cancel-button {
	flex: 1;
}
</style>
